import React, { useCallback, useEffect, useState } from 'react';

import { Group, Modal, Text, Title, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import usePayRates from 'hooks/payRate/usePayRates';
import { CellProps } from 'react-table';
import { PayRate } from 'types';

import DeletePayRateModal from './DeletePayRateModal';
import DrawerPayRateForm from './DrawerPayRateForm';

const PayRatesTableContainer = ({
  searchTerm,
  permission = true,
}: {
  searchTerm: string;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { formatCurrency } = useLocalization();

  const [ordering, setOrdering] = useState('name');
  const [ascending, setAscending] = useState(true);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    usePayRates({
      search: searchTerm,
      ordering: ascending ? ordering : `-${ordering}`,
    });

  const [tableData, setTableData] = useState<PayRate[]>([]);
  const [selectedPayRate, setSelectedPayRate] = useState<PayRate>();
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [isEditFormOpen, { open: openEditForm, close: closeEditForm }] = useDisclosure(false);

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(!ascending);
    },
    [ascending],
  );

  const handleCloseEditForm = (): void => {
    setSelectedPayRate(undefined);
    closeEditForm();
  };

  const handleEdit = (payRate: PayRate): void => {
    setSelectedPayRate(payRate);
    openEditForm();
  };

  const handleDelete = (payRate: PayRate): void => {
    setSelectedPayRate(payRate);
    openDeleteModal();
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: ({ row }: CellProps<PayRate>) => (
        <Text
          onClick={() => handleEdit(row.original)}
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
        >
          {row.original.name}
        </Text>
      ),
    },
    {
      Header: 'Rate',
      accessor: 'rate' as const,
      Cell: ({ row }: CellProps<PayRate>) => {
        if (row.original.payRateType === 'PERCENTAGE') {
          return `${((row.original.percentage ?? 0) * 100).toFixed(2)}%`;
        }
        return formatCurrency((row.original.rate ?? 0) / 100);
      },
    },
    {
      Header: 'Payment Summary',
      Cell: ({ row }: CellProps<PayRate>) => {
        if (row.original.payRateType === 'PERCENTAGE') {
          return 'Percentage Rate';
        }
        if (row.original.additionalPerAttendee) {
          return `Attendance Plus ${row.original.payRateType === 'BASE' ? 'Base' : 'Hourly'} Rate`;
        } else {
          return `${row.original.payRateType === 'BASE' ? 'Base' : 'Hourly'} Rate Only`;
        }
      },
    },
    {
      id: 'action',
      Cell: ({ row }: CellProps<PayRate>) => (
        <Group position='right' px='xs'>
          <Menu
            disabled={!permission}
            data-cy='rowButtonPayRate'
            position='bottom-end'
            items={[
              {
                title: 'Edit',
                icon: <IconEdit size={theme.fontSizes.sm} />,
                onClick: () => handleEdit(row.original),
              },
              {
                title: 'Delete',
                icon: <IconTrashX size={theme.fontSizes.sm} />,
                color: 'red',
                onClick: () => handleDelete(row.original),
              },
            ]}
          />
        </Group>
      ),
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['name']}
        handleHeaderClick={handleHeaderClick}
        ascending={ascending}
        ordering={ordering}
      />
      <DrawerPayRateForm
        payRateId={selectedPayRate?.payRateId}
        opened={isEditFormOpen}
        closeHandler={handleCloseEditForm}
        formId='edit-pay-rate-form'
        title={<Title order={3}>{permission ? 'Edit' : 'View'} Pay Rate</Title>}
        type='edit'
        permission={permission}
      />
      <Modal
        opened={isDeleteModalOpen}
        onClose={closeDeleteModal}
        title={<Title order={4}>Delete Pay Rate</Title>}
        trapFocus
        centered
        size='auto'
      >
        <DeletePayRateModal payRate={selectedPayRate} handleClose={closeDeleteModal} />
      </Modal>
    </>
  );
};

export default PayRatesTableContainer;
